import React from 'react';
import classNames from 'classnames';
import { ModalInMobile, Button } from '../../../components';
import { useIntl } from '../../../util/reactIntl';
import { ReactComponent as ArrowLeft } from '../../../assets/icons/arrow-left.svg';

import css from './FilterDetailModal.module.css';

/**
 * FilterDetailModal - A full-screen modal for editing individual filters on mobile
 *
 * @component
 * @param {Object} props
 * @param {string} [props.id] - Unique id for this modal instance, used to key its own
 *   scroll-lock request (see ui.duck.js's disableScrollRequests) - every filter that
 *   renders a FilterDetailModal (club/league/player/country/season/verified/price) is
 *   mounted at once on the search page, so this must be unique per filter or they all
 *   fight over the same shared scroll-lock/open-state record.
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Close handler
 * @param {string} props.title - Modal title (filter name)
 * @param {React.ReactNode} props.children - Filter content (search + checkboxes)
 * @param {Function} props.onManageDisableScrolling - Scrolling management function
 * @param {number} props.showAsModalMaxWidth - Max width for mobile modal
 * @param {number} [props.resultsCount] - When given, shows the same green "Show items" button
 *   the main filter screen has, at the bottom of this one too (client feedback: users only
 *   found the top-left back button by accident, not realizing it's how selections here get
 *   confirmed). Omit to render without it (e.g. if a results count isn't meaningful here).
 * @param {string} [props.className] - Additional class names
 * @returns {JSX.Element}
 */
const FilterDetailModal = props => {
  const {
    id = 'FilterDetailModal',
    isOpen,
    onClose,
    title,
    children,
    onManageDisableScrolling,
    showAsModalMaxWidth,
    resultsCount,
    className,
  } = props;
  const intl = useIntl();

  const classes = classNames(css.root, className);
  const hasResultsCount = typeof resultsCount === 'number';
  const showListingsLabel = hasResultsCount
    ? intl.formatMessage({ id: 'SearchFiltersMobile.showListings' }, { count: resultsCount })
    : null;

  return (
    <ModalInMobile
      id={`FilterDetailModal.${id}`}
      isModalOpenOnMobile={isOpen}
      onClose={onClose}
      showAsModalMaxWidth={showAsModalMaxWidth}
      onManageDisableScrolling={onManageDisableScrolling}
      containerClassName={css.modalContainer}
      closeButtonMessage=""
      // Renders into #portal-root instead of wherever this component happens to be nested -
      // see SearchFiltersMobile.js's own usePortal for why that's required for the elevated
      // z-index (FilterDetailModal.module.css) to actually beat AppInstallBanner.
      usePortal
    >
      <div className={classes}>
        <div className={css.header}>
          <button type="button" className={css.backButton} onClick={onClose}>
            <ArrowLeft className={css.backIcon} width={24} height={24} />
          </button>
          <h2 className={css.title}>{title}</h2>
        </div>
        <div className={css.content}>{children}</div>
      </div>
      {hasResultsCount ? (
        <div className={css.showListingsContainer}>
          <Button className={css.showListingsButton} onClick={onClose}>
            {showListingsLabel}
          </Button>
        </div>
      ) : null}
    </ModalInMobile>
  );
};

export default FilterDetailModal;
